import React from "react";
import {Box, Divider, Flex, FormControl, FormLabel, Heading, Switch, Text, useToast} from "@chakra-ui/react";
import {useMessageAlerts, useSetMessageAlerts} from "./hooks";

// "A secure message is waiting" (blhWebServer ADR 0018 §7): when the clinic
// writes and the patient has not read it in 15 minutes, they are told by email
// or text, without what it says. On by default; each channel is the patient's
// to turn off. A text needs their consent to texts first, given in "Text
// messages" above, never by this switch. Shown only where the clinic sends
// these alerts (the platform switched it on: `offered`).

const CHANNELS = [
  {
    key: "email",
    label: "Email me when a message is waiting",
    on: "On: an email tells you a secure message is waiting",
    off: "Off: no email about new messages",
    unavailable: "Needs an email address on file. Ask your clinic to add one.",
  },
  {
    key: "sms",
    label: "Text me when a message is waiting",
    on: "On: a text tells you a secure message is waiting",
    off: "Off: no text about new messages",
    unavailable: "Turn on text messages above first.",
  },
];

export function MessageAlertsSettings({dashState}) {
  const auth = {jwt: dashState?.jwt};
  const patientId = dashState?.patient?.pid;
  const alerts = useMessageAlerts(auth, {patientId, enabled: true});
  const setAlerts = useSetMessageAlerts(auth, patientId);
  const toast = useToast();

  const change = (key, value) =>
    setAlerts.mutate(
      {[key]: value},
      {
        onError: (error) =>
          toast({
            status: "error",
            title: "Not changed",
            description: error?.message || "Your message alerts could not be changed. Try again.",
          }),
      }
    );

  // A clinic that does not send these alerts yet offers no choice: nothing is shown.
  if (alerts.data && !alerts.data.offered) return null;

  return (
    <Box aria-labelledby="message-alerts-heading" role="group">
      <Divider my={4} borderColor="gray.100" />
      <Heading as="h4" id="message-alerts-heading" fontSize="sm" fontWeight="medium" mb={1}>
        New message alerts
      </Heading>
      <Text fontSize="xs" color="gray.500" mb={3}>
        If you have not read a message from your clinic within 15 minutes, we let you know. We never include what it says:
        you read it here, after you sign in.
      </Text>
      {CHANNELS.map((channel, index) => {
        const state = alerts.data?.[channel.key];
        const available = state?.available === true;
        const on = state?.on === true;
        const id = `message-alerts-${channel.key}`;
        return (
          <FormControl key={channel.key} mt={index === 0 ? 0 : 4} isDisabled={!alerts.data || !available}>
            <Flex align="center" justify="space-between">
              <Box mr={4}>
                <FormLabel htmlFor={id} mb={0} fontSize="sm" fontWeight="medium">
                  {channel.label}
                </FormLabel>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {alerts.isError ? "Could not load. Reload the page to try again." : !alerts.data ? "Loading…" : !available ? channel.unavailable : on ? channel.on : channel.off}
                </Text>
              </Box>
              <Switch
                id={id}
                isChecked={on}
                isDisabled={!alerts.data || !available || setAlerts.isPending}
                colorScheme="purple"
                onChange={() => change(channel.key, !on)}
              />
            </Flex>
          </FormControl>
        );
      })}
    </Box>
  );
}
